import { Injectable } from '@angular/core';
import { HttpHeaders } from '@angular/common/http';
import { AbstractAuthenticationService } from '../authentication/abstract-authentication.service';
import { AbstractStateService } from '../state/abstract-state.service';
import { ServerCallerService } from './server-caller.service';
import {
  ResponseError,
  HEADER_KEY_AUTHORIZATION,
  HEADER_KEY_CONTENT_TYPE,
  HEADER_VALUE_CONTENT_TYPE_APPLICATION_JSON,
  HEADER_KEY_X_API_KEY,
} from '../../models';
import { HttpCallerService } from './http-caller.service';

@Injectable({ providedIn: 'root' })
export class PrivateServerCallerService {
  private readonly BEARER_TOKEN_PREFIX = 'Bearer';

  constructor(
    private stateService: AbstractStateService,
    private serverCaller: ServerCallerService,
    private authenticationService: AbstractAuthenticationService
  ) {}

  public async get(
    path: string,
    headers?: HttpHeaders,
    responseType = HttpCallerService.RESPONSE_TYPE.JSON
  ): Promise<any> {
    try {
      return await this.serverCaller.get(
        path,
        this.addTokenToHeader(headers),
        responseType
      );
    } catch (e) {
      return this.handleError(e);
    }
  }

  public async post(
    path: string,
    data: any,
    headers?: HttpHeaders,
    responseType = HttpCallerService.RESPONSE_TYPE.JSON
  ): Promise<any> {
    try {
      return await this.serverCaller.post(
        path,
        data,
        this.addTokenToHeader(headers),
        responseType
      );
    } catch (e) {
      return this.handleError(e);
    }
  }

  public async put(
    path: string,
    data: any,
    headers?: HttpHeaders,
    responseType = HttpCallerService.RESPONSE_TYPE.JSON
  ): Promise<any> {
    try {
      return await this.serverCaller.put(
        path,
        data,
        this.addTokenToHeader(headers),
        responseType
      );
    } catch (e) {
      return this.handleError(e);
    }
  }

  public async patch(
    path: string,
    data: any,
    headers?: HttpHeaders,
    responseType = HttpCallerService.RESPONSE_TYPE.JSON
  ): Promise<any> {
    try {
      return await this.serverCaller.patch(
        path,
        data,
        this.addTokenToHeader(headers),
        responseType
      );
    } catch (e) {
      return this.handleError(e);
    }
  }

  public async delete(
    path: string,
    data?: any,
    headers?: HttpHeaders,
    responseType = HttpCallerService.RESPONSE_TYPE.JSON
  ): Promise<any> {
    try {
      return await this.serverCaller.delete(
        path,
        data,
        this.addTokenToHeader(headers),
        responseType
      );
    } catch (e) {
      return this.handleError(e);
    }
  }

  private addTokenToHeader(headers: HttpHeaders): HttpHeaders {
    const headersToAddToken = headers
      ? headers
      : new HttpHeaders({
          [HEADER_KEY_CONTENT_TYPE]: HEADER_VALUE_CONTENT_TYPE_APPLICATION_JSON,
        });

    const token = this.stateService.getToken();
    const headerKeyAuth = token.startsWith(this.BEARER_TOKEN_PREFIX)
      ? HEADER_KEY_AUTHORIZATION
      : HEADER_KEY_X_API_KEY;

    return headersToAddToken.set(headerKeyAuth, this.stateService.getToken());
  }

  private async handleError(e: any) {
    const error: ResponseError = e.error;

    if (error?.status === 401) {
      await this.authenticationService.logout();
      throw e;
    } else {
      throw e;
    }
  }
}
